'use client';
import React, { useContext } from 'react';
import cn from 'classnames';
import { KibBreadcrumbs, KibBreadcrumbsItem } from '@chewy/kib-menus-react';
import '@chewy/kib-menus-styles/dist/styles/kib-breadcrumbs.css';
import styles from './BreadcrumbsContainer.module.scss';
import OHCContext from '@/providers/OHCContext';
import { navigateToAnchor } from '@/utils/helpCenterUtils';
import { useRouter } from 'next/navigation';
import first from 'lodash/first';
import { useTrack } from '@chewy/use-analytics';

interface Topic {
  id: string;
  title: string;
  sections: Array<{ id: string; title: string; categoryId: string }>;
  categoryId: string;
}
interface Article {
  id: string;
  title: string;
  categoryId: string;
}
interface BreadcrumbsContainerProps {
  className?: string;
}

const BreadcrumbsContainer: React.FC<BreadcrumbsContainerProps> = ({ className }) => {
  const { selectedTopic, setSelectedTopic, selectedArticle, setSelectedArticle, setSelectedSection, isDesktopMode } = useContext(OHCContext);
  const router = useRouter();
  const track = useTrack();
  const onTopicClick = (topic?: Topic) => {
    const section = topic ? first(topic.sections) : null;
    if (topic) {
      setSelectedTopic(topic);
      if (isDesktopMode) {
        setSelectedArticle(null); // Reset article when a new topic is selected
      } else {
        setSelectedSection(section);
      }
      setSelectedArticle(null); // Reset article when a new topic is selected
    } else {
      setSelectedTopic(null);
      setSelectedSection(null);
      setSelectedArticle(null); // Reset all selections when no topic is provided
    }

    const eventProperties = {
      id: 'breadcrumbs-topic-button-id',
      parentId: 'breadcrumbs-id',
      level: 'breadcrumbs',
      type: 'static',
      position: 1,
      topicId: topic?.id,
      title: topic?.title,
      categoryId: topic?.categoryId,
      sectionId: section?.id,
      sectionTitle: section?.title,
      sectionCategoryId: section?.categoryId
    };
    track('Breadcrumbs Topic Button Clicked', eventProperties);
    navigateToAnchor(isDesktopMode ? topic?.id : section?.id, router);
  };

  if (!selectedTopic) {
    return null; // or return a placeholder if no topic is selected
  }

  return (
    <nav className={cn(styles.breadcrumbs, className)} aria-label="Breadcrumb navigation">
      <KibBreadcrumbs>
        <KibBreadcrumbsItem
          onClick={() => {
            onTopicClick();
          }}
          aria-label="Go to Help Center home"
        >
          Help
        </KibBreadcrumbsItem>
        {selectedArticle ? (
          <KibBreadcrumbsItem
            onClick={() => {
              onTopicClick(selectedTopic);
            }}
            aria-label={`Go to ${selectedTopic.title} topic`}
          >
            {selectedTopic.title}
          </KibBreadcrumbsItem>
        ) : (
          <KibBreadcrumbsItem aria-label={`Current page: ${selectedTopic?.title}`} current={true}>
            {selectedTopic.title}
          </KibBreadcrumbsItem>
        )}
        {selectedArticle && (
          <KibBreadcrumbsItem current aria-current="page" aria-label={`Current page: ${selectedArticle?.title}`}>
            {selectedArticle?.title}
          </KibBreadcrumbsItem>
        )}
      </KibBreadcrumbs>
    </nav>
  );
};

export default BreadcrumbsContainer;
